import {
  Add as AddIcon,
  DeleteOutline as DeleteOutlineIcon,
  EditOutlined as EditOutlinedIcon,
  GroupAdd as GroupAddIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import {
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import useNoti from "../../../hooks/useNoti";
import { useTranslation } from "../../../i18n/useTranslation";
import { AppNotifications } from "../../../models";
import { EmployeeService } from "../../../services/employee.service";
import Loading from "../../components/Loading";
import "./index.scss";

type TeamListItem = {
  _id: string;
  name: string;
  memberCount?: number;
  createdAt?: string;
  leader?: {
    _id: string;
    code?: string;
    fullName?: string;
  } | null;
};

type TeamMember = {
  _id: string;
  code?: string;
  fullName?: string;
  email?: string;
};

type TeamDetail = TeamListItem & {
  members: TeamMember[];
};

type EmployeeOption = {
  _id: string;
  code?: string;
  fullName?: string;
  team?: { _id?: string; name?: string } | null;
};

const Teams: React.FC = () => {
  const { t } = useTranslation();
  const [pushNoti] = useNoti();
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState<TeamListItem[]>([]);
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);

  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [saving, setSaving] = useState(false);

  const [editTeam, setEditTeam] = useState<TeamListItem | null>(null);
  const [editName, setEditName] = useState("");
  const [editLeader, setEditLeader] = useState<EmployeeOption | null>(null);

  const [deleteTeam, setDeleteTeam] = useState<TeamListItem | null>(null);

  const [detail, setDetail] = useState<TeamDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [addMembersOpen, setAddMembersOpen] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState<EmployeeOption[]>([]);

  const fetchTeams = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const result = await EmployeeService.getEmployeeTeam();
      setTeams(result.data.data || []);
    } catch (error) {
      console.error(error);
      pushNoti(AppNotifications.ERROR, t("teams.loadListFailed"));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [pushNoti, t]);

  const fetchEmployees = useCallback(async () => {
    try {
      const result = await EmployeeService.getEmployee();
      setEmployees(result.data.data || []);
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    fetchTeams();
    fetchEmployees();
  }, [fetchTeams, fetchEmployees]);

  const openDetail = async (teamId: string) => {
    try {
      setDetailLoading(true);
      const result = await EmployeeService.getEmployeeTeamById(teamId);
      setDetail(result.data.data || null);
    } catch (error) {
      console.error(error);
      pushNoti(AppNotifications.ERROR, t("teams.loadDetailFailed"));
    } finally {
      setDetailLoading(false);
    }
  };

  const createTeam = async () => {
    const name = createName.trim();
    if (!name) return;
    try {
      setSaving(true);
      await EmployeeService.createEmployeeTeam({ name });
      setCreateOpen(false);
      setCreateName("");
      pushNoti(AppNotifications.SUCCESS, t("teams.created"));
      fetchTeams(true);
    } catch (error: any) {
      pushNoti(
        AppNotifications.ERROR,
        error?.response?.data?.message || t("teams.createFailed")
      );
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async () => {
    if (!editTeam) return;
    const name = editName.trim();
    if (!name) return;
    try {
      setSaving(true);
      await EmployeeService.updateEmployeeTeam(editTeam._id, {
        name,
        leaderId: editLeader?._id || null,
      });
      setEditTeam(null);
      pushNoti(AppNotifications.SUCCESS, t("teams.updated"));
      fetchTeams(true);
      if (detail?._id === editTeam._id) {
        openDetail(editTeam._id);
      }
    } catch (error: any) {
      pushNoti(
        AppNotifications.ERROR,
        error?.response?.data?.message || t("teams.updateFailed")
      );
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTeam) return;
    try {
      setSaving(true);
      await EmployeeService.deleteEmployeeTeam(deleteTeam._id);
      setDeleteTeam(null);
      if (detail?._id === deleteTeam._id) setDetail(null);
      pushNoti(AppNotifications.SUCCESS, t("teams.deleted"));
      fetchTeams(true);
    } catch (error: any) {
      pushNoti(
        AppNotifications.ERROR,
        error?.response?.data?.message || t("teams.deleteFailed")
      );
    } finally {
      setSaving(false);
    }
  };

  const memberIds = useMemo(
    () => new Set((detail?.members || []).map((m) => String(m._id))),
    [detail]
  );

  const availableEmployees = useMemo(
    () => employees.filter((emp) => !memberIds.has(String(emp._id))),
    [employees, memberIds]
  );

  const openAddMembers = () => {
    setSelectedMembers([]);
    setAddMembersOpen(true);
  };

  const saveMembers = async () => {
    if (!detail) return;
    const nextIds = [
      ...Array.from(memberIds),
      ...selectedMembers.map((item) => String(item._id)),
    ];
    try {
      setSaving(true);
      const result = await EmployeeService.setEmployeeTeamMembers(
        detail._id,
        nextIds
      );
      setDetail((current) =>
        current
          ? {
              ...current,
              members: result.data.data?.members || [],
              memberCount: result.data.data?.memberCount || 0,
            }
          : current
      );
      setAddMembersOpen(false);
      pushNoti(AppNotifications.SUCCESS, t("teams.memberAdded"));
      fetchTeams(true);
      fetchEmployees();
    } catch (error: any) {
      pushNoti(
        AppNotifications.ERROR,
        error?.response?.data?.message || t("teams.addMemberFailed")
      );
    } finally {
      setSaving(false);
    }
  };

  const removeMember = async (employeeId: string) => {
    if (!detail) return;
    const nextIds = (detail.members || [])
      .map((m) => String(m._id))
      .filter((id) => id !== String(employeeId));
    try {
      setSaving(true);
      const result = await EmployeeService.setEmployeeTeamMembers(
        detail._id,
        nextIds
      );
      setDetail((current) =>
        current
          ? {
              ...current,
              members: result.data.data?.members || [],
              memberCount: result.data.data?.memberCount || 0,
            }
          : current
      );
      pushNoti(AppNotifications.SUCCESS, t("teams.memberRemoved"));
      fetchTeams(true);
      fetchEmployees();
    } catch (error: any) {
      pushNoti(
        AppNotifications.ERROR,
        error?.response?.data?.message || t("teams.removeMemberFailed")
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  const dash = t("common.dash");

  return (
    <div className="teams-listing">
      <div className="teams-listing__header">
        <h2>{t("teams.title")}</h2>
        <div className="teams-listing__header-actions">
          <Button startIcon={<RefreshIcon />} onClick={() => fetchTeams()}>
            {t("common.refresh")}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setCreateName("");
              setCreateOpen(true);
            }}
          >
            {t("teams.create")}
          </Button>
        </div>
      </div>

      <div className="teams-listing__summary">
        <div className="teams-listing__card">
          {t("teams.totalTeams", { count: teams.length })}
        </div>
        <div className="teams-listing__card">
          {t("teams.totalMembers", {
            count: teams.reduce((sum, team) => sum + (team.memberCount || 0), 0),
          })}
        </div>
      </div>

      <div className="teams-listing__layout">
        <div className="teams-listing__table">
          <div className="teams-listing__row teams-listing__row--head">
            <div>{t("teams.name")}</div>
            <div>{t("teams.leader")}</div>
            <div>{t("teams.members")}</div>
            <div>{t("common.actions")}</div>
          </div>
          {teams.length === 0 ? (
            <div className="teams-listing__empty">{t("teams.empty")}</div>
          ) : (
            teams.map((team) => (
              <div className="teams-listing__row" key={team._id}>
                <div>
                  <button
                    type="button"
                    className="teams-listing__link"
                    onClick={() => openDetail(team._id)}
                  >
                    <strong>{team.name}</strong>
                  </button>
                </div>
                <div>
                  {team.leader
                    ? `${team.leader.fullName || dash} (${team.leader.code || dash})`
                    : dash}
                </div>
                <div>{team.memberCount || 0}</div>
                <div className="teams-listing__actions">
                  <Button size="small" onClick={() => openDetail(team._id)}>
                    {t("common.view")}
                  </Button>
                  <Button
                    size="small"
                    startIcon={<EditOutlinedIcon />}
                    onClick={() => {
                      setEditTeam(team);
                      setEditName(team.name);
                      setEditLeader(
                        team.leader
                          ? {
                              _id: String(team.leader._id),
                              code: team.leader.code,
                              fullName: team.leader.fullName,
                            }
                          : null
                      );
                    }}
                  >
                    {t("common.edit")}
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={() => setDeleteTeam(team)}
                  >
                    {t("common.delete")}
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="teams-listing__detail">
          {!detail && !detailLoading && (
            <div className="teams-listing__empty">
              Chọn một team để xem thành viên.
            </div>
          )}
          {detailLoading && <Loading />}
          {detail && !detailLoading && (
            <>
              <div className="teams-listing__detail-header">
                <div>
                  <h3>{detail.name}</h3>
                  <p>{detail.memberCount || 0} thành viên</p>
                  <p className="teams-listing__muted">
                    {t("teams.leader")}:{" "}
                    {detail.leader
                      ? `${detail.leader.fullName || dash} (${detail.leader.code || dash})`
                      : t("teams.noLeader")}
                  </p>
                </div>
                <Button
                  variant="outlined"
                  startIcon={<GroupAddIcon />}
                  onClick={openAddMembers}
                >
                  {t("teams.addMember")}
                </Button>
              </div>
              <div className="teams-listing__members">
                {(detail.members || []).length === 0 ? (
                  <div className="teams-listing__empty">
                    {t("teams.noMembers")}
                  </div>
                ) : (
                  detail.members.map((member) => (
                    <div className="teams-listing__member" key={member._id}>
                      <div>
                        <strong>{member.fullName || dash}</strong>
                        <div className="teams-listing__muted">
                          {member.code || dash}
                          {member.email ? ` · ${member.email}` : ""}
                        </div>
                      </div>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => removeMember(member._id)}
                        disabled={saving}
                      >
                        Gỡ
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t("teams.createTitle")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label={t("teams.name")}
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") createTeam();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>{t("common.cancel")}</Button>
          <Button
            variant="contained"
            onClick={createTeam}
            disabled={saving || !createName.trim()}
          >
            {t("teams.create")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editTeam)} onClose={() => setEditTeam(null)} fullWidth maxWidth="xs">
        <DialogTitle>{t("teams.editTitle")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label={t("teams.name")}
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") saveEdit();
            }}
          />
          <Autocomplete
            options={employees}
            value={editLeader}
            onChange={(_, value) => setEditLeader(value)}
            getOptionLabel={(option) =>
              `${option.fullName || dash} (${option.code || dash})`
            }
            isOptionEqualToValue={(option, value) =>
              String(option._id) === String(value._id)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                margin="dense"
                label={t("teams.leader")}
                placeholder={t("teams.pickLeader")}
                helperText={t("teams.leaderHelp")}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTeam(null)}>{t("common.cancel")}</Button>
          <Button
            variant="contained"
            onClick={saveEdit}
            disabled={saving || !editName.trim()}
          >
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTeam)} onClose={() => setDeleteTeam(null)} fullWidth maxWidth="xs">
        <DialogTitle>{t("teams.deleteTitle")}</DialogTitle>
        <DialogContent>
          {t("teams.deleteConfirm", { name: deleteTeam?.name || "" })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTeam(null)}>{t("common.cancel")}</Button>
          <Button color="error" variant="contained" onClick={confirmDelete} disabled={saving}>
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addMembersOpen}
        onClose={() => setAddMembersOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {t("teams.addMemberTitle", { name: detail?.name || "" })}
        </DialogTitle>
        <DialogContent>
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={availableEmployees}
            value={selectedMembers}
            onChange={(_, value) => setSelectedMembers(value)}
            getOptionLabel={(option) =>
              `${option.fullName || dash} (${option.code || dash})`
            }
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox checked={selected} style={{ marginRight: 8 }} />
                {option.fullName || dash} ({option.code || dash})
                {option.team?.name
                  ? t("teams.inTeam", { name: option.team.name })
                  : ""}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="dense"
                label={t("teams.pickEmployee")}
                placeholder={t("teams.searchEmployee")}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddMembersOpen(false)}>{t("common.cancel")}</Button>
          <Button
            variant="contained"
            onClick={saveMembers}
            disabled={saving || selectedMembers.length === 0}
          >
            {t("teams.addMember")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Teams;
